/**
 * NotificationSection — preference toggles for all notification categories.
 * Lives inside Settings → Notifications tab.
 *
 * Preferences are persisted to the backend via GET/PUT /v1/notifications/preferences.
 * `securityAlerts` is always on and cannot be toggled.
 */
import React, { useEffect, useState } from 'react';
import { Icons } from '@/src/modules/components/Icons';
import { SettingsCard, SettingRow, ToggleSwitch } from '@/src/modules/components/settings/primitives';
import { useToast } from '@/src/modules/components/settings/ToastProvider';
import {
  getNotificationPreferences,
  updateNotificationPreferences,
  type NotificationPreferences,
} from '@/src/infrastructure/lib/api';

type ToggleKey = keyof Omit<NotificationPreferences, 'securityAlerts'>;

const DEFAULTS: NotificationPreferences = {
  emailNotifications: true,
  rsvpNotifications: true,
  guestActivityUpdates: true,
  reminderNotifications: true,
  marketingEmails: false,
  securityAlerts: true,
  invitationNotifications: true,
  creditNotifications: true,
  billingNotifications: true,
  feedbackNotifications: true,
  supportNotifications: true,
  accountNotifications: true,
};

type ItemDef = {
  key: keyof NotificationPreferences;
  label: string;
  description: string;
  locked?: boolean;
  group?: string;
};

const ITEMS: ItemDef[] = [
  // ── Master ──────────────────────────────────────────────────────────────
  {
    key: 'emailNotifications',
    label: 'Email notifications',
    description: 'Master switch — turns all email from InviteCloud on or off.',
    group: 'General',
  },
  {
    key: 'securityAlerts',
    label: 'Security alerts',
    description: 'New sign-ins and password changes. Always on for your safety.',
    locked: true,
    group: 'General',
  },
  {
    key: 'marketingEmails',
    label: 'Marketing emails',
    description: 'Product news, tips and seasonal offers.',
    group: 'General',
  },

  // ── Invitations & events ─────────────────────────────────────────────
  {
    key: 'invitationNotifications',
    label: 'Invitation lifecycle',
    description: 'When your invitation is published, expires, archived, or restored.',
    group: 'Invitations & events',
  },
  {
    key: 'rsvpNotifications',
    label: 'RSVP responses',
    description: 'Get notified the moment a guest responds to your invitation.',
    group: 'Invitations & events',
  },
  {
    key: 'reminderNotifications',
    label: 'Event reminders',
    description: 'Countdowns and pending-task nudges before your event.',
    group: 'Invitations & events',
  },
  {
    key: 'guestActivityUpdates',
    label: 'Guest activity',
    description: 'Views, opens and shares on your live invitation.',
    group: 'Invitations & events',
  },

  // ── Credits & billing ───────────────────────────────────────────────
  {
    key: 'creditNotifications',
    label: 'Publish credits',
    description: 'When credits are granted, used, or revoked from your account.',
    group: 'Credits & billing',
  },
  {
    key: 'billingNotifications',
    label: 'Payments',
    description: 'Payment confirmations and failure alerts.',
    group: 'Credits & billing',
  },

  // ── Support & feedback ──────────────────────────────────────────────
  {
    key: 'feedbackNotifications',
    label: 'Feedback updates',
    description: "Status changes and replies on feedback you've submitted.",
    group: 'Support & feedback',
  },
  {
    key: 'supportNotifications',
    label: 'Support tickets',
    description: 'Replies and status updates on your support tickets.',
    group: 'Support & feedback',
  },

  // ── Account ─────────────────────────────────────────────────────────
  {
    key: 'accountNotifications',
    label: 'Account changes',
    description: 'Profile updates, email changes and account verification.',
    group: 'Account',
  },
];

// Group items by their group label for the section headings
const GROUPS = ['General', 'Invitations & events', 'Credits & billing', 'Support & feedback', 'Account'];

export function NotificationSection() {
  const { toast } = useToast();
  const [prefs, setPrefs] = useState<NotificationPreferences>(DEFAULTS);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let active = true;
    getNotificationPreferences()
      .then((p) => active && setPrefs(p))
      .catch(() => active && toast('Could not load notification settings.', 'error'))
      .finally(() => active && setLoading(false));
    return () => { active = false; };
  }, [toast]);

  async function toggle(key: ToggleKey, next: boolean) {
    const previous = prefs;
    setPrefs((p) => ({ ...p, [key]: next }));
    setSaving(true);
    try {
      const updated = await updateNotificationPreferences({ [key]: next });
      setPrefs(updated);
      toast(next ? 'Notification enabled.' : 'Notification disabled.', 'info');
    } catch {
      setPrefs(previous);
      toast('Could not save — please try again.', 'error');
    } finally {
      setSaving(false);
    }
  }

  const masterOff = !prefs.emailNotifications;

  return (
    <div className="space-y-6">
      {GROUPS.map((group) => {
        const groupItems = ITEMS.filter((item) => item.group === group);
        if (groupItems.length === 0) return null;

        return (
          <SettingsCard
            key={group}
            icon={Icons.Bell}
            title={group}
            description={GROUP_DESCRIPTIONS[group]}
          >
            {groupItems.map((item) => {
              const isMasterSwitch = item.key === 'emailNotifications';
              const disabledByMaster =
                !isMasterSwitch &&
                !item.locked &&
                masterOff &&
                item.key !== 'securityAlerts';

              return (
                <SettingRow
                  key={item.key}
                  label={item.label}
                  description={item.description}
                  htmlFor={`notif-${item.key}`}
                >
                  <ToggleSwitch
                    id={`notif-${item.key}`}
                    checked={item.locked ? true : prefs[item.key]}
                    disabled={item.locked || loading || saving || disabledByMaster}
                    onChange={(next) => toggle(item.key as ToggleKey, next)}
                  />
                </SettingRow>
              );
            })}
          </SettingsCard>
        );
      })}
    </div>
  );
}

const GROUP_DESCRIPTIONS: Record<string, string> = {
  'General':             'Master switches and cross-category email settings.',
  'Invitations & events':'Notifications related to your invitations and guest engagement.',
  'Credits & billing':   'Credit balance changes and payment activity.',
  'Support & feedback':  'Updates on your support tickets and submitted feedback.',
  'Account':             'Changes to your account profile and security.',
};
